import { useEffect, useState } from 'react';
import { fetchUserContracts } from '../services/userService';
import type { ContractSummary } from '../services/types';

/** Zustand der geladenen Mietvertraege eines Benutzers. */
export interface UserContractsState {
  /** Vertraege oder null (kein Benutzer gewaehlt, laedt noch, Fehler). */
  readonly data: readonly ContractSummary[] | null;
  /** true, waehrend ein Request laeuft. */
  readonly loading: boolean;
  /** Deutsche Fehlermeldung oder null. */
  readonly error: string | null;
}

/** Fehlermeldung fuer fehlgeschlagene Vertrags-Requests. */
const LOAD_ERROR_MESSAGE = 'Die Mietverträge konnten nicht geladen werden.';

/** Geladenes Ergebnis samt zugehoeriger Benutzer-ID. */
interface LoadedContracts {
  readonly key: string;
  readonly data: readonly ContractSummary[] | null;
  readonly error: string | null;
}

/**
 * Laedt die Mietvertraege des gewaehlten Benutzers (kein Caching,
 * Projektstandard). `userId === null` bedeutet: kein Benutzer gewaehlt.
 *
 * Stale-Guard wie in useTimeSeries: Vertraege werden erst angezeigt, wenn
 * sie zur aktuell gewaehlten Benutzer-ID gehoeren — sonst stuenden
 * kurzzeitig fremde Vertraege unter dem neuen Benutzer. Das geladene
 * Ergebnis wird zusaetzlich beim START jedes Requests verworfen, damit
 * ein Wechsel A -> B -> A nicht kurz die alten Vertraege ohne
 * Ladeindikator zeigt.
 *
 * Fehler werden still behandelt (KEIN console.error).
 */
export function useUserContracts(userId: string | null): UserContractsState {
  const [loaded, setLoaded] = useState<LoadedContracts | null>(null);

  useEffect(() => {
    setLoaded(null);
    if (userId === null) {
      return undefined;
    }
    const controller = new AbortController();
    fetchUserContracts(userId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        setLoaded({ key: userId, data: response, error: null });
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        setLoaded({ key: userId, data: null, error: LOAD_ERROR_MESSAGE });
      });
    return () => {
      controller.abort();
    };
  }, [userId]);

  if (userId === null) {
    return { data: null, loading: false, error: null };
  }
  const isCurrent = loaded !== null && loaded.key === userId;
  return {
    data: isCurrent ? loaded.data : null,
    loading: !isCurrent,
    error: isCurrent ? loaded.error : null,
  };
}

export default useUserContracts;
